import { ApolloError } from '@apollo/client';

import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { DocumentDetails_DocumentSummaryFragment } from '@/modules/documents/graphql/DocumentDetails.generated';
import { getNodes } from '@/utils/graphqlUtils';

import { useReviewDocumentSummaryTrowserQuery } from '../graphql/ReviewDocumentSummaryTrowser.generated';

export interface ReviewDocumentSummary {
  id: string;
  documentSummary?: DocumentDetails_DocumentSummaryFragment;
}

export interface UseReviewDocumentSummaryResult {
  loading: boolean;
  document: ReviewDocumentSummary | null;
}

export function useReviewDocumentSummary(
  documentId: string,
  skip: boolean
): UseReviewDocumentSummaryResult {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const { data, loading } = useReviewDocumentSummaryTrowserQuery({
    variables: { documentIds: [documentId] },
    skip,
    onError: (error: ApolloError) => {
      reportError('Failed to load document for review', error);
      showFeedback(FeedbackMessages.queryError);
    },
  });

  const documents = getNodes(data?.documents);
  if (loading) {
    return { loading: true, document: null };
  }

  const document = documents[0];

  if (!document) {
    return { loading: false, document: null };
  }

  // we query summaries here, but we expect to only have one summary because the document was just uploaded
  // and the user has no way of adding multiple summaries at this point
  const documentSummary = documents[0]?.summaries?.[0];

  return {
    loading: false,
    document: {
      id: document.id,
      documentSummary: documentSummary ?? undefined,
    },
  };
}
